import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { PawPrint, ArrowRight, ShieldCheck } from "lucide-react";
import { IMAGES, scrollToId } from "@/data/content";

const HEADLINE_LINES = ["Help your senior poodle", "feel like themselves", "again."];
const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 60]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="cbdoodle-hero-container"
      className="relative overflow-hidden bg-[#FAF7F2] px-5 pb-16 pt-28 sm:px-8 sm:pt-36 lg:pb-24"
    >
      {/* ambient glows */}
      <motion.div
        style={{ y: blobY }}
        className="pointer-events-none absolute -right-24 top-10 h-[420px] w-[420px] rounded-full bg-[#C58F3B]/12 blur-[90px]"
      />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[360px] w-[360px] rounded-full bg-[#4A6B5D]/12 blur-[90px]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-12 lg:gap-8">
        {/* Left */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#E6E0D4] bg-[#FFFDF9] px-4 py-1.5"
          >
            <PawPrint size={14} className="text-[#4A6B5D]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A6B5D]">
              A Concept for the Golden Years
            </span>
          </motion.div>

          <h1
            data-testid="cbdoodle-hero-headline"
            className="font-serif text-[2.6rem] font-semibold leading-[1.02] tracking-tight text-[#2C2B29] sm:text-6xl lg:text-[4.4rem]"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: reduce ? 0 : "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease }}
                >
                  {i === 1 ? (
                    <span className="italic text-[#4A6B5D]">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease }}
            className="mt-6 max-w-xl font-sans text-base leading-relaxed text-[#5C5850] sm:text-lg"
          >
            CBDoodle is a premium CBD concept — thoughtfully imagined chews & tinctures to support
            joint comfort, easy movement, and calm, restful days as your poodle — and every doodle — grows older.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.72, ease }}
            className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
          >
            <button
              data-testid="cbdoodle-hero-primary-cta"
              onClick={() => scrollToId("waitlist")}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C58F3B] px-7 py-3.5 font-sans text-[15px] font-medium text-white shadow-sm transition-all duration-200 hover:bg-[#B07D2B] hover:shadow-lg active:scale-[0.98] sm:w-auto"
            >
              Join the Waitlist
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="cbdoodle-hero-secondary-cta"
              onClick={() => scrollToId("products")}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#4A6B5D]/30 px-7 py-3.5 font-sans text-[15px] font-medium text-[#2D4338] transition-all duration-200 hover:border-[#4A6B5D] hover:bg-[#E2ECE7]/50 sm:w-auto"
            >
              Explore the Formula
            </button>
          </motion.div>

          <motion.div
            data-testid="cbdoodle-hero-trust-badge"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 flex items-center gap-2.5 text-[#5C5850]"
          >
            <ShieldCheck size={18} className="text-[#4A6B5D]" />
            <span className="font-sans text-sm">
              Vet-guided approach · Third-party lab focus · Low-THC (&lt;0.3%)
            </span>
          </motion.div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease }}
            className="relative mx-auto max-w-md"
          >
            <div className="relative overflow-hidden rounded-[2rem] border border-[#E6E0D4] bg-[#FFFDF9] shadow-[0_30px_80px_-30px_rgba(44,43,41,0.35)]">
              <img
                src={IMAGES.hero}
                alt="Elegant senior poodle looking happy and comfortable"
                className="h-[440px] w-full object-cover sm:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2B29]/25 via-transparent to-transparent" />
            </div>

            <motion.div
              className="animate-float absolute -left-4 bottom-10 rounded-2xl border border-[#E6E0D4] bg-[#FFFDF9]/95 px-4 py-3 shadow-lg backdrop-blur sm:-left-8"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8478]">Comfort Score</div>
              <div className="font-serif text-2xl font-semibold text-[#2D4338]">Calm &amp; Cozy</div>
            </motion.div>

            <div className="absolute -right-3 top-8 rounded-full border border-[#E6E0D4] bg-[#FFFDF9]/95 px-4 py-2 shadow-lg backdrop-blur sm:-right-6">
              <span className="flex items-center gap-1.5 font-sans text-xs font-medium text-[#2C2B29]">
                <span className="h-2 w-2 rounded-full bg-[#4A6B5D]" /> Senior Poodle Approved
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
